import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';

import { CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DialogSettings, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';

import { RewardTokensContainerComponent, RewardTokensContainerMode } from '../components/digital/reward-tokens-container.component';
import { RewardTokensInfoComponent } from '../components/digital/reward-tokens-info.component';
import { RewardTokenContext } from '../reward-tokens.model';

interface RewardTokensDialogPayload {
    tokenContext: RewardTokenContext;
    trackingSource?: Record<string, string>;
}

@Injectable()
export class RewardTokensDialogService {
    private tokensModalDialogRef: ModalRef | null = null;
    private infoModalDialogRef: ModalRef | null = null;
    private injector = inject(Injector);
    private readonly _doc = inject(DOCUMENT);

    constructor(
        private modalDialogService: ModalDialogService,
        private epcotConfigService: EpcotConfigService,
        private tokensConfig: CrmConfig,
    ) {}

    openTokensDialog(inColumn: boolean, payload: RewardTokensDialogPayload): void {
        this.closeInfoDialog();
        this.closeTokensDialog();

        const settings = this.getModalDialogSettings(inColumn, false);

        this.tokensModalDialogRef = this.modalDialogService.openDialog(RewardTokensContainerComponent, {
            data: {
                mode: inColumn ? RewardTokensContainerMode.Desktop : RewardTokensContainerMode.Mobile,
                tokenContext: payload.tokenContext,
                trackingSource: payload.trackingSource,
            },
            settings,
        });

        if (this.tokensModalDialogRef) {
            this.tokensModalDialogRef.result.catch(() => this.closeTokensDialog());
        }
    }

    openInfoDialog(inColumn: boolean, tokenContext: RewardTokenContext): void {
        this.closeInfoDialog();
        inColumn = !this.tokensConfig.tokens.isRewardDrawerEnabled;
        const settings = this.getModalDialogSettings(inColumn, true);

        this.infoModalDialogRef = this.modalDialogService.openDialog(RewardTokensInfoComponent, {
            data: {
                tokenContext,
                inOverlay: false,
                modelDialog: true,
            },
            settings,
        });

        if (this.infoModalDialogRef) {
            this.infoModalDialogRef.result.catch(() => this.closeInfoDialog());
        }
    }

    closeTokensDialog(): void {
        if (this.tokensModalDialogRef) {
            this.tokensModalDialogRef.close();
            this.tokensModalDialogRef = null;
        }

        this.closeInfoDialog();
    }

    closeInfoDialog(): void {
        if (this.infoModalDialogRef) {
            this.infoModalDialogRef.close();
            this.infoModalDialogRef = null;
        }
    }

    private getModalDialogSettings(inColumn: boolean, isInfoDialog: boolean): DialogSettings {
        const modalDialogContainer = inColumn ? this._doc.querySelector<HTMLElement>('.column-right .bet-column') : null;
        const epcotClass = this.epcotConfigService.isEnabled(EpcotModule.Betslip) ? 'epcot-enabled' : '';

        const infoClass = isInfoDialog ? 'reward-tokens-dialog--info' : '';
        const infoBackdropClass = isInfoDialog ? 'reward-tokens-modal-backdrop--info' : '';

        const settings: DialogSettings = {
            fit: inColumn,
            isModalDialog: false,
            cssClass: `reward-tokens-dialog betslip-vars-scope ${infoClass} ${epcotClass}`,
            inColumn,
            container: modalDialogContainer ?? undefined,
            scrollFreezeType: inColumn ? ScrollFreezeServiceTypes.None : ScrollFreezeServiceTypes.All,
            backdropClass: `reward-tokens-modal-backdrop ${infoBackdropClass}`,
            injector: this.injector,
        };

        return settings;
    }
}
